import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { EMPTY, Observable, ReplaySubject, Subject, finalize, map } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../auth-service/auth-service.service';
import { BulkUtils } from '../../ui/BulkUtils';
import { operationActionEnum, operationEnum } from '../../common/enum/bulkExportEnum';
import { PageChangeType } from '../../common/enum/PageChangeTypeEnum';
import { BulkOperationSourceEnum } from '../../ui/Node/MedicinalProductsNode/BulkOperationSourceEnum';
import { TrackerServiceComponent } from '../tracker-service/tracker.service';

@Injectable({
  providedIn: 'root'
})
export class BulkService {

  private readonly stepChangeSubject =
    new ReplaySubject<{ stepIndex: number; operation: BulkOperationSourceEnum }>(1);
  readonly stepChange$ = this.stepChangeSubject.asObservable();

  private readonly refreshSubject =
    new Subject<BulkOperationSourceEnum>();
  readonly refresh$ = this.refreshSubject.asObservable();

  private readonly pageChangeSubject =
    new Subject<{ type: PageChangeType; operation: BulkOperationSourceEnum }>();
  readonly pageChange$ = this.pageChangeSubject.asObservable();

  constructor(private injector: Injector,
    private httpClient: HttpClient) { }

  notifyStepChange(stepIndex: number, operation: BulkOperationSourceEnum): void {
    this.stepChangeSubject.next({ stepIndex, operation });
  }

  notifyRefresh(operation: BulkOperationSourceEnum): void {
    this.refreshSubject.next(operation);
  }

  notifyPageChange(type: PageChangeType, operation: BulkOperationSourceEnum): void {
    this.pageChangeSubject.next({ type, operation });
  }

  preview(criteria: string): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.injector.get(AuthService).getAccessToken(),
      AuthSession: this.injector.get(AuthService).getSessionToken()
    });

    // when requesting a preview the preview filter must not be included
    const criteriaTransformed = BulkUtils.cleanModel(BulkUtils.bulkModelTransform(this.injector, criteria), ['previews']);
    const formData = new FormData();
    formData.append("criteria", new Blob(
      [JSON.stringify(criteriaTransformed)],
      { type: "application/json" }
    ));

    return this.httpClient.post(`${environment.apiUrl}/api/bulk/preview`, formData, { headers: headers, responseType: 'json' });
  }

  count(criteria: string): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.injector.get(AuthService).getAccessToken(),
      AuthSession: this.injector.get(AuthService).getSessionToken()
    });

    // when requesting a preview the preview filter must not be included
    const criteriaTransformed = BulkUtils.cleanModel(BulkUtils.bulkModelTransform(this.injector, criteria), []);
    const formData = new FormData();
    formData.append("criteria", new Blob(
      [JSON.stringify(criteriaTransformed)],
      { type: "application/json" }
    ));

    return this.httpClient.post(`${environment.apiUrl}/api/bulk/count`, formData, { headers: headers, responseType: 'json' });
  }

  request(criteria: string): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.injector.get(AuthService).getAccessToken(),
      AuthSession: this.injector.get(AuthService).getSessionToken()
    });

    // when requesting a preview the preview filter must not be included
    const criteriaTransformed = BulkUtils.cleanModel(BulkUtils.bulkModelTransform(this.injector, criteria), []);
    const formData = new FormData();
    formData.append("criteria", new Blob(
      [JSON.stringify(criteriaTransformed)],
      { type: "application/json" }
    ));

    return this.httpClient.post(`${environment.apiUrl}/api/bulk/request`, formData, { headers, responseType: 'json' });
  }

  operationRequest(criteria: any, operation: operationEnum): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.injector.get(AuthService).getAccessToken(),
      AuthSession: this.injector.get(AuthService).getSessionToken()
    });

    const formData = new FormData();
    formData.append("id", new Blob(
      [JSON.stringify(criteria.id)],
      { type: "application/json" }
    ));

    return this.httpClient.post(`${environment.apiUrl}/api/bulk/${operation}`, formData, { headers: headers, responseType: 'blob', observe: 'response' });
  }

  public actionTrackerKey: string = '';

  operationActions(criteria: any, operation: operationActionEnum, data?: any): Observable<any> {
    const headers = new HttpHeaders({
      Authorization: 'Bearer ' + this.injector.get(AuthService).getAccessToken(),
      AuthSession: this.injector.get(AuthService).getSessionToken()
    });

    this.actionTrackerKey = `bulk-${criteria.code}-${data.action}`;

    if (operation === operationActionEnum.update) {
      if (!this.injector.get(TrackerServiceComponent).start(this.actionTrackerKey)) {
        return EMPTY;
      }
    }

    const formData = new FormData();

    formData.append("id", new Blob(
      [JSON.stringify(criteria.id)],
      { type: "application/json" }
    ));

    switch (operation) {
      case operationActionEnum.save:
      case operationActionEnum.update:
        formData.append("data", new Blob(
          [JSON.stringify(data)],
          { type: "application/json" }
        ));
        break;
    }

    return this.httpClient.post(`${environment.apiUrl}/api/bulk/${operation}`, formData, { headers: headers, responseType: 'json', observe: 'response' })
    .pipe(
      finalize(() => {
        if (operation === operationActionEnum.update) {
            this.injector.get(TrackerServiceComponent).stop(this.actionTrackerKey);
        }
      })
    );
  }
}